import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { AppBar } from '../components/layout/AppBar';
import { FamilyDirectory } from '../components/family/FamilyDirectory';
import { FamilyMap } from '../components/maps/FamilyMap';
import { AdminSettings } from '../components/admin/AdminSettings';
import { BirthdayCalendarPage } from './BirthdayCalendarPage';
import { MessagesPage } from './MessagesPage';
import { HomePage } from './HomePage';
import { ProfileModal } from '../components/profile/ProfileModal';
import { PWADebug } from '../components/debug/PWADebug';
import { User } from '../types';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setCurrentConversation } from '../store/reducers/messages.reducer';
import { getFamilyMembers } from '../api/userApi';
import authService from '../services/auth';
import { useMessaging } from '../hooks/useMessaging';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loginUser: user } = useAppSelector((state) => state.user);
  const { conversations } = useAppSelector((state) => state.messages);
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalUser, setProfileModalUser] = useState<User | null>(null);

  // Initialize messaging globally so it works on all pages
  useMessaging();

  // Calculate total unread messages
  const totalUnreadMessages = conversations.reduce((total, conv) => total + conv.unreadCount, 0);

  useEffect(() => {
    const loadFamilyMembers = async () => {
      try {
        const response = await getFamilyMembers();

        if (response.data?.success && response.data.data?.members) {
          setFamilyMembers(response.data.data.members);
        } else {
          if (user) {
            setFamilyMembers([user]);
          }
        }
      } catch (error) {
        console.error('Dashboard: Failed to load family members:', error);
        if (user) {
          setFamilyMembers([user]);
        }
      }
    };

    if (user) {
      loadFamilyMembers();

      const intervalId = setInterval(() => {
        loadFamilyMembers();
      }, 30000);

      return () => clearInterval(intervalId);
    }
  }, [user]);

  const handleContactMember = (member: User, contactType: 'phone' | 'email') => {
    if (contactType === 'phone' && (member.phoneNumber || member.phone_number)) {
      window.open(`tel:${member.phoneNumber || member.phone_number}`);
    } else if (contactType === 'email') {
      window.open(`mailto:${member.email}`);
    }
  };

  const handleViewLocation = (member: User) => {
    setBottomNavValue(1);
    navigate('/dashboard/map');
  };

  const handleEditProfile = async (member: User) => {
    try {
      // Fetch the user's full profile data
      const profileData = await authService.getUserProfile(member.id);
      setProfileModalUser(profileData);
      setProfileModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // Fallback to using the member data we have
      setProfileModalUser(member);
      setProfileModalOpen(true);
    }
  };

  const handleProfileModalClose = () => {
    setProfileModalOpen(false);
    setProfileModalUser(null);
  };

  const handleProfileUpdate = (updatedUser: User) => {
    setProfileModalUser(updatedUser);

    // Update the family members list to reflect the changes
    setFamilyMembers(prevMembers =>
      prevMembers.map(member =>
        member.id === updatedUser.id ? updatedUser : member
      )
    );
  };

  const handleLocationUpdate = (location: { lat: number; lng: number }) => {
    console.log('Location updated:', location);
  };

  const handleOpenChat = (member: User) => {
    dispatch(setCurrentConversation(String(member.id)));
    navigate('/dashboard/messages');
    setBottomNavValue(3);
    setProfileModalOpen(false);
  };

  const handleBottomNavChange = (newValue: number) => {
    setBottomNavValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/dashboard/home');
        break;
      case 1:
        navigate('/dashboard/directory');
        break;
      case 2:
        navigate('/dashboard/calendar');
        break;
      case 3:
        navigate('/dashboard/messages');
        break;
      case 4:
        if (user) {
          handleEditProfile(user);
        }
        break;
      case 5:
        navigate('/dashboard/settings');
        break;
    }
  };

  return (
    <div style={{ background: 'transparent'}}>
      <AppBar />

      <main className="flex-grow-1" style={{ background: 'transparent' }}>
        <Routes>
          <Route
            path="/"
            element={<HomePage />}
          />
          <Route
            path="/home"
            element={<HomePage />}
          />
          <Route
            path="/directory"
            element={
              <FamilyDirectory
                members={familyMembers}
                onContactMember={handleContactMember}
                onViewLocation={handleViewLocation}
                onEditProfile={handleEditProfile}
                onOpenChat={handleOpenChat}
              />
            }
          />
          <Route
            path="/map"
            element={
              <div style={{ height: 'calc(100vh - 64px - 80px)', padding: '16px' }}>
                <FamilyMap
                  members={familyMembers}
                  onLocationUpdate={handleLocationUpdate}
                />
              </div>
            }
          />
          <Route
            path="/settings"
            element={<AdminSettings />}
          />
          <Route
            path="/calendar"
            element={<BirthdayCalendarPage />}
          />
          <Route
            path="/messages"
            element={<MessagesPage />}
          />
          <Route
            path="/pwa-debug"
            element={<PWADebug />}
          />
        </Routes>
      </main>

      <div className="bottom-nav paper-elevation-3">
        <button
          className={`bottom-nav-action glassBtn ${bottomNavValue === 0 ? 'selected' : ''}`}
          onClick={() => handleBottomNavChange(0)}
          title="Home"
        >
          <span className="material-icons bottom-nav-icon">home</span>
        </button>
        <button
          className={`bottom-nav-action glassBtn ${bottomNavValue === 1 ? 'selected' : ''}`}
          onClick={() => handleBottomNavChange(1)}
          title="Family Directory"
        >
          <span className="material-icons bottom-nav-icon">people</span>
        </button>
        <button
          className={`bottom-nav-action glassBtn ${bottomNavValue === 2 ? 'selected' : ''}`}
          onClick={() => handleBottomNavChange(2)}
          title="Birthday Calendar"
        >
          <span className="material-icons bottom-nav-icon">event</span>
        </button>
        <button
          className={`bottom-nav-action glassBtn ${bottomNavValue === 3 ? 'selected' : ''}`}
          onClick={() => handleBottomNavChange(3)}
          style={{ position: 'relative' }}
          title="Messages"
        >
          <span className="material-icons bottom-nav-icon">chat_bubble</span>
          {totalUnreadMessages > 0 && (
            <span style={{
              position: 'absolute',
              top: '4px',
              right: '8px',
              backgroundColor: '#f44336',
              color: 'white',
              borderRadius: '50%',
              minWidth: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              padding: '0 4px',
              border: '2px solid var(--primary-dark)',
              zIndex: 1
            }}>
              {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
            </span>
          )}
        </button>
        {/* <button
          className={`bottom-nav-action glassBtn ${bottomNavValue === 4 ? 'selected' : ''}`}
          onClick={() => handleBottomNavChange(4)}
        >
          <span className="material-icons bottom-nav-icon">person</span>
        </button>
        <button
          className={`bottom-nav-action glassBtn ${bottomNavValue === 5 ? 'selected' : ''}`}
          onClick={() => handleBottomNavChange(5)}
        >
          <span className="material-icons bottom-nav-icon">settings</span>
        </button> */}
      </div>

      <ProfileModal
        open={profileModalOpen}
        onClose={handleProfileModalClose}
        user={profileModalUser}
        currentUser={user}
        onContact={handleContactMember}
        onViewLocation={handleViewLocation}
        onOpenChat={handleOpenChat}
        onUpdate={handleProfileUpdate}
      />
    </div>
  );
};